import classNames from "classnames";
import { useEffect, useRef } from "react";

import useApplyFilter from "components/Filters/useApplyFilter";
import { NoteFind } from "components/icons/generated";
import ListEntitiesItem from "components/ListEntitiesItem";
import Timestamp from "components/Timestamp";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import { useDrawerVisibilityForId } from "ds/components/DrawerNew/useDrawerVisibilityForId";
import DropdownMenu from "ds/components/DropdownMenu";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Link from "ds/components/Link";
import TextEllipsis from "ds/components/TextEllipsis";
import Typography from "ds/components/Typography";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageAuditTrail } from "hooks/useAnalytics/pages/auditTrail";
import { AuditTrailEntry } from "types/generated";

import {
  AuditTrailEntrySuggestions,
  COLUMN_GAP,
  COLUMN_ORDER,
  FILTER_ITEMS_DICTIONARY,
} from "../../constants";
import { formatMetadata } from "../../helpers";
import CreatedBy from "./CreatedBy";
import EventTypeBadge from "./EventTypeBadge";
import styles from "./styles.module.css";

type AuditTrailListItemProps = {
  item: AuditTrailEntry;
  setRowHeight?: (size: number) => void;
  onOpenDetails: () => void;
};

function AuditTrailListItem({ item, setRowHeight, onOpenDetails }: AuditTrailListItemProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageAuditTrail.Logs,
  });

  const isActive = useDrawerVisibilityForId(item.id);

  const handleRowHeight = () => {
    if (setRowHeight && rowRef.current) {
      setRowHeight(rowRef.current.getBoundingClientRect().height);
    }
  };

  useEffect(handleRowHeight);

  const { applyFilter } = useApplyFilter<AuditTrailEntrySuggestions>({
    searchSuggestionsDictionary: FILTER_ITEMS_DICTIONARY,
  });

  const {
    typeLabel: affectedResourceTypeLabel,
    detailLabel: affectedResourceDetailLabel,
    detailUrl: affectedResourceDetailUrl,
  } = formatMetadata(item.affectedResource, item.action);

  const {
    typeLabel: relatedResourceTypeLabel,
    detailLabel: relatedResourceDetailLabel,
    detailUrl: relatedResourceDetailUrl,
  } = formatMetadata(item.relatedResource, item.action);

  const onAffectedResourceLinkClicked = () => {
    trackSegmentAnalyticsEvent("Link clicked", {
      column: "Affected resource",
    });
  };

  const onRelatedResourceLinkClicked = () => {
    trackSegmentAnalyticsEvent("Link clicked", {
      column: "Related resource",
    });
  };

  return (
    <ListEntitiesItem
      align="center"
      grid
      gridTemplate={COLUMN_ORDER}
      gap={`0 ${COLUMN_GAP}`}
      ref={rowRef}
      isActive={isActive}
    >
      <Box align="start" justify="center" direction="column" gap="small" grow="0">
        <Typography tag="span" variant="p-body3">
          {affectedResourceTypeLabel}
        </Typography>
        {affectedResourceDetailLabel && (
          <DropdownMenu
            triggerComponent={
              <TextEllipsis tooltip={affectedResourceDetailLabel} tooltipWidthMode="maxWidthSm">
                {(ellipsisProps) => (
                  <Link
                    {...ellipsisProps}
                    onPress={onAffectedResourceLinkClicked}
                    disabled={!item.affectedResource.resourceId}
                    className={classNames(ellipsisProps.className, styles.link)}
                  >
                    <Typography variant="p-t7" tag="span">
                      {affectedResourceDetailLabel}
                    </Typography>
                  </Link>
                )}
              </TextEllipsis>
            }
          >
            {item.affectedResource.resourceId && (
              <DropdownMenuItem
                onAction={() => {
                  applyFilter(AuditTrailEntrySuggestions.AffectedResourceID)(
                    item.affectedResource.resourceId!
                  );
                }}
              >
                Add resource to filters
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onAction={() => {
                applyFilter(AuditTrailEntrySuggestions.AffectedResourceType)(
                  item.affectedResource.resourceType
                );
              }}
            >
              Add resource type to filters
            </DropdownMenuItem>
            {affectedResourceDetailUrl && (
              <DropdownMenuItem href={affectedResourceDetailUrl}>
                Go to {affectedResourceTypeLabel}
              </DropdownMenuItem>
            )}
          </DropdownMenu>
        )}
      </Box>
      <Box align="start" justify="center" direction="column" gap="small">
        {item.relatedResource ? (
          <>
            <Typography tag="span" variant="p-body3">
              {relatedResourceTypeLabel}
            </Typography>
            {relatedResourceTypeLabel && (
              <DropdownMenu
                triggerComponent={
                  <TextEllipsis tooltip={relatedResourceDetailLabel} tooltipWidthMode="maxWidthSm">
                    {(ellipsisProps) => (
                      <Link
                        {...ellipsisProps}
                        onPress={onRelatedResourceLinkClicked}
                        disabled={!item.relatedResource!.resourceId}
                        className={classNames(ellipsisProps.className, styles.link)}
                      >
                        <Typography variant="p-t7" tag="span">
                          {relatedResourceDetailLabel}
                        </Typography>
                      </Link>
                    )}
                  </TextEllipsis>
                }
              >
                {item.relatedResource!.resourceId && (
                  <DropdownMenuItem
                    onAction={() => {
                      applyFilter(AuditTrailEntrySuggestions.RelatedResourceID)(
                        item.relatedResource!.resourceId!
                      );
                    }}
                  >
                    Add resource to filters
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onAction={() => {
                    applyFilter(AuditTrailEntrySuggestions.RelatedResourceType)(
                      item.relatedResource!.resourceType
                    );
                  }}
                >
                  Add resource type to filters
                </DropdownMenuItem>
                {relatedResourceDetailUrl && (
                  <DropdownMenuItem href={relatedResourceDetailUrl}>
                    Go to {relatedResourceTypeLabel}
                  </DropdownMenuItem>
                )}
              </DropdownMenu>
            )}
          </>
        ) : (
          <Typography tag="span" variant="p-body3">
            -
          </Typography>
        )}
      </Box>
      <Box align="center">
        <EventTypeBadge eventType={item.eventType} />
      </Box>
      <Box align="center">
        <Typography tag="span" variant="s-body3" className={styles.action}>
          {item.action}
        </Typography>
      </Box>
      <Box align="center">
        <CreatedBy applyFilter={applyFilter} value={item.actor} />
      </Box>
      <Box align="center">
        <Timestamp timestamp={item.createdAt} color="primary" />
      </Box>
      <Box align="center">
        <ButtonIcon icon={NoteFind} size="small" variant="secondary" onPress={onOpenDetails}>
          Show log details
        </ButtonIcon>
      </Box>
    </ListEntitiesItem>
  );
}

export default AuditTrailListItem;
